import { AgenciaOrcamentoPolicy } from './agenciaOrcamentoPolicy';
import { LOCAL_EXTERIOR, localPagamentoLinha } from './moedaFornecedorPolicy';
import { isCoordenacaoRemota } from './coordenacaoRemotaPolicy';

// Chiesi: fornecedor Axis identifica serviço próprio. A API revalida os IDs
// e o CNPJ no tenant; aqui a mesma política mantém o total imediato da grade.
const CNPJ_AXIS = '21415772000164';
const codigoProduto = (produto = {}) => String(produto?.codigo ?? '').trim();
const idComparavel = (valor) => String(
    valor && typeof valor === 'object' ? (valor.id ?? '') : (valor ?? '')
).trim();
const documento = (valor) => String(valor ?? '').replace(/\D/g, '');
const isLinhaImposto = (subitem = {}) => ['1945', 'IMPOSTO_NF_ACCESS'].includes(
    codigoProduto(subitem.produto).toUpperCase()
) || Boolean(subitem.linhaVirtualImpostoNfAccess);

const isGrupoServicosAgencia = (grupo = {}) => {
    const nome = String(grupo?.produto?.nome ?? '')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();
    return codigoProduto(grupo.produto) === '3027' || nome === 'SERVICOS AGENCIA';
};

// Planilha Chiesi (04/10): na COORDENAÇÃO a NF 19,05% incide sobre o bloco inteiro, inclusive o
// principal da linha paga no exterior (Coordenação Local, alimentação/transporte). Espelho de
// OrcamentoChiesiImpostoPolicy (API).
const isGrupoCoordenacao = (grupo = {}) => {
    const nome = String(grupo?.produto?.nome ?? '')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase().replace(/^\d+\s*-\s*/, '');
    return codigoProduto(grupo.produto) === '3026' || nome === 'COORDENACAO';
};

// D-0092 (Chiesi 23/10): o aéreo da Access não leva a NF Access, só o fee aéreo (Serviços Agência).
// Pelo nome: o código 63 também é "Taxi". Espelho de OrcamentoChiesiImpostoPolicy (API).
const isGrupoAereo = (grupo = {}) => String(grupo?.produto?.nome ?? '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toUpperCase().replace(/^\d+\s*-\s*/, '') === 'AEREO';

const isServicoProprio = (subitem = {}, fornecedorAxis = null) => {
    // Imposto nunca entra na própria base, mesmo quando seu fornecedor é Axis.
    if (isLinhaImposto(subitem)) {
        return false;
    }
    if (AgenciaOrcamentoPolicy.isFeeAgenciaGlobal(subitem.produto)) {
        return true;
    }
    return documento(subitem?.fornecedor?.cnpj) === CNPJ_AXIS
        || (Boolean(idComparavel(fornecedorAxis))
            && idComparavel(subitem.fornecedor) === idComparavel(fornecedorAxis));
};

const isAereoDaAccess = (grupo = {}, subitem = {}, fornecedorAxis = null) => isGrupoAereo(grupo)
    && isServicoProprio(subitem, fornecedorAxis);

const aplicar = (grupos = [], clienteEhChiesi = false, fornecedorAxis = null) => {
    if (!clienteEhChiesi || !Array.isArray(grupos)) {
        return;
    }
    grupos.forEach((grupo) => {
        (grupo.subitem || []).forEach((subitem) => {
            if (isGrupoServicosAgencia(grupo) || isLinhaImposto(subitem) || isAereoDaAccess(grupo, subitem, fornecedorAxis)) {
                // 3027 já contém a linha física: não somar imposto virtual também. Aéreo da Access: nunca
                // (limpa também a marca gravada pela regra de 16/09).
                subitem.inserirCalculoTotalPlanilha = false;
                subitem.impostoNfAccessAutomaticoChiesi = false;
                return;
            }
            const automatico = isServicoProprio(subitem, fornecedorAxis);
            const principalExterior = !automatico && isGrupoCoordenacao(grupo) && localPagamentoLinha(subitem) === LOCAL_EXTERIOR
                && !isCoordenacaoRemota(subitem.produto);
            if (principalExterior) {
                subitem.inserirCalculoTotalPlanilha = true;
                subitem.nfAccessPrincipalExteriorChiesi = true;
                subitem.impostoNfAccessAutomaticoChiesi = false;
                return;
            }
            if (subitem.nfAccessPrincipalExteriorChiesi === true) {
                // Saiu do exterior / da Coordenação: não herdar a marcação.
                subitem.inserirCalculoTotalPlanilha = false;
            }
            delete subitem.nfAccessPrincipalExteriorChiesi;
            if (automatico) {
                subitem.inserirCalculoTotalPlanilha = true;
            } else if (subitem.impostoNfAccessAutomaticoChiesi === true) {
                // Troca Axis/FEE -> terceiro: não herdar a seleção automática antiga.
                subitem.inserirCalculoTotalPlanilha = false;
            }
            // Flags manuais dos demais itens permanecem intactas.
            subitem.impostoNfAccessAutomaticoChiesi = automatico;
        });
    });
};

// Sanofi (Inter 13/10): só a regra da COORDENAÇÃO (diárias, RDV de alimentação/transporte) — o principal pago
// no exterior entra na base da NF Access 19,05%, como na Chiesi. Nada das demais regras da Chiesi (AGÊNCIA,
// 1945, FEE). Fora da Sanofi a função só desfaz uma marcação que tenha ficado. Espelho da API
// (OrcamentoChiesiImpostoPolicy::marcarCoordenacaoExterior).
const aplicarCoordenacaoExterior = (grupos = [], ativo = false, fornecedorAxis = null) => {
    if (!Array.isArray(grupos)) {
        return;
    }
    grupos.forEach((grupo) => {
        (grupo.subitem || []).forEach((subitem) => {
            const marcar = ativo
                && isGrupoCoordenacao(grupo)
                && !isLinhaImposto(subitem)
                && !isServicoProprio(subitem, fornecedorAxis)
                && localPagamentoLinha(subitem) === LOCAL_EXTERIOR
                && !isCoordenacaoRemota(subitem.produto);
            if (marcar) {
                subitem.inserirCalculoTotalPlanilha = true;
                subitem.nfAccessPrincipalExteriorChiesi = true;
                return;
            }
            if (subitem.nfAccessPrincipalExteriorChiesi === true) {
                subitem.inserirCalculoTotalPlanilha = false;
                delete subitem.nfAccessPrincipalExteriorChiesi;
            }
        });
    });
};

export const ChiesiImpostoPolicy = { isServicoProprio, isAereoDaAccess, aplicar, aplicarCoordenacaoExterior };
